import { Button, Card, CardBody, InfoTooltip, ProgressBar } from '@/common'
import { EmissionReportStatus } from '@/api/EmissionReportModel'
import { UnitLabel } from '@/shared/config/config'
import useI18n from '@/shared/hooks/useI18n'
import { useCallback, useMemo } from 'react'
import { ShareArrowIcon } from '@/common/components/icons'
import ReportDirectEmissionsTable from './components/ReportDirectEmissionsTable'

export default function SingleMethodEmissionReportStatusControlSection({
  submittedAt,
  reportStatus,
  reportProgress,
  submit,
  isActivityLevelRequired,
  isSubmitting,
  activityLevel,
  canEditReport,
  onActivityLevelEdit,
  formattedTotalDirect,
  formattedTotalIndirect,
  formattedSpecificDirect,
  formattedSpecificIndirect
}: {
  submittedAt: string | null
  activityLevel: number | null
  reportStatus: EmissionReportStatus
  reportProgress: number
  submit: () => void
  isActivityLevelRequired: boolean
  isSubmitting: boolean
  canEditReport: boolean
  onActivityLevelEdit: () => void
  formattedTotalDirect: string
  formattedTotalIndirect: string
  formattedSpecificDirect: string
  formattedSpecificIndirect: string
}) {
  const { t, dateFormatter } = useI18n()

  const isSubmitDisabled = useMemo(() => {
    return (
      reportProgress !== 100 ||
      reportStatus === EmissionReportStatus.SUBMITTED ||
      isSubmitting ||
      !canEditReport
    )
  }, [reportProgress, reportStatus, isSubmitting, canEditReport])

  const handleSubmit = useCallback(() => {
    submit()
  }, [submit])

  const isSubmitted = reportStatus === EmissionReportStatus.SUBMITTED && submittedAt
  const formattedSubmittedOn = isSubmitted ? dateFormatter(new Date(submittedAt)) : ''

  return (
    <div className="px-6">
      <Card>
        <CardBody className="flex flex-col md:flex-row gap-5 min-h-40">
          {isActivityLevelRequired && (
            <div className="flex flex-col justify-between py-2 md:w-1/5 gap-4">
              <div className="flex items-center gap-1">
                <h4 className="text-sm">{t('report:activityLevel.title')}</h4>
                <InfoTooltip content={t('report:activityLevel.fields.activityLevel.hint')} />
              </div>
              <div className="flex justify-center font-medium">
                {activityLevel} {UnitLabel.t}
              </div>
              <div>
                <Button
                  variant="subtle"
                  sizeType="sm"
                  className="w-full"
                  onClick={onActivityLevelEdit}
                  disabled={reportStatus === EmissionReportStatus.SUBMITTED}
                >
                  {t('report:activityLevel.editActivityLevel')}
                </Button>
              </div>
            </div>
          )}
          <div className="flex-1">
            <ReportDirectEmissionsTable
              formattedTotalDirect={formattedTotalDirect}
              formattedTotalIndirect={formattedTotalIndirect}
              formattedSpecificDirect={formattedSpecificDirect}
              formattedSpecificIndirect={formattedSpecificIndirect}
            />
          </div>
          <div className="md:w-1/5 flex flex-col justify-between py-2 gap-4">
            <div className="flex items-center gap-1">
              <h4 className="text-sm">{t('report:emissionReportStatusControl.title')}</h4>
              <InfoTooltip content={t('report:emissionReportStatusControl.reportStatusTooltip')} />
            </div>
            <div className="w-full flex align-top h-full">
              <ProgressBar value={reportProgress} maxValue={100} />
            </div>
            {isSubmitted && (
              <p className="flex items-center gap-2 text-sm text-text-secondary">
                <span>
                  <ShareArrowIcon className="size-6 " />
                </span>
                {t('report:emissionReportStatusControl.submittedOn', {
                  date: formattedSubmittedOn
                })}
              </p>
            )}
            <div>
              <Button
                sizeType="sm"
                icon={<ShareArrowIcon />}
                disabled={isSubmitDisabled}
                className="w-full"
                onClick={handleSubmit}
                isLoading={isSubmitting}
              >
                {t('report:emissionReportStatusControl.submitReport')}
              </Button>
            </div>
          </div>
        </CardBody>
      </Card>
    </div>
  )
}
